import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { AUTHORS } from "../data/authors";

/**
 * Author profile page.
 *
 * Currently supports a single author (Aaron Bankhurst). Structured to
 * accept future authors via /authors/:slug if needed. Emits Person JSON-LD
 * with an `alumniOf`/`worksFor` link back to the Ohio Accident Aid
 * Organization. No credentials, awards, or licenses are asserted here or
 * in the schema — see /editorial-policy for the editorial standard.
 */
export default function AuthorProfile() {
  const author = AUTHORS["Aaron Bankhurst"];

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: author.name,
    jobTitle: author.role,
    description: author.bio,
    url: author.profileUrl,
    worksFor: {
      "@type": "Organization",
      name: "Ohio Accident Aid",
      url: "https://ohioaccidentaid.com",
    },
  };

  return (
    <>
      <SEO
        title={`${author.name} — ${author.role}`}
        description={author.bio}
        type="profile"
        url={author.profileUrl}
        schema={personSchema}
      />
      <section className="min-h-[60vh] px-6 py-16 md:py-24 bg-bone" data-testid="author-profile">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="text-sm font-semibold text-charcoal-soft hover:text-terracotta"
            data-testid="author-back-home"
          >
            ← Back to home
          </Link>

          <div className="mt-8 border-b border-black/10 pb-8">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta mb-3">
              {author.role}
            </p>
            <h1
              className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight leading-[1.05]"
              data-testid="author-name"
            >
              {author.name}
            </h1>
          </div>

          <div className="mt-8 prose-content" data-testid="author-bio">
            <p className="text-lg text-charcoal-soft leading-relaxed">{author.bio}</p>

            <h2 className="text-2xl font-display font-bold text-forest mt-10 mb-3">
              Editorial approach
            </h2>
            <p className="text-charcoal-soft leading-relaxed">
              Ohio Accident Aid is an attorney-matching service, not a law firm.
              Content on this site is intended to help unrepresented Ohio accident
              victims understand their options; it is not legal advice. We prefer
              primary sources — the Ohio Revised Code, published court opinions,
              official state and federal agencies, and municipal codes — and we
              publish corrections when we get something wrong. Read our full{" "}
              <Link
                to="/editorial-policy"
                className="text-terracotta underline"
                data-testid="author-editorial-policy-link"
              >
                editorial policy
              </Link>
              .
            </p>

            <h2 className="text-2xl font-display font-bold text-forest mt-10 mb-3">
              Reviewer note
            </h2>
            <p className="text-charcoal-soft leading-relaxed">
              Where an article has been reviewed by a credentialed professional —
              a licensed Ohio attorney, physician, or subject-matter expert — that
              reviewer is named separately on the article. Aaron's byline reflects
              authorship and editorial oversight only; it does not represent legal,
              medical, or investigative credentials.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
